import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import CircularProgress from 'components/shared/CircularProgress';
import useWorkflow from 'hooks/useWorkflow';
import { simpleDate, simpleDateAtTime } from 'lib/date';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, BodySmallBold, H2 } from 'styles/typography';
import BackArrowIcon from 'svg/BackArrowIcon';

import CategoryCard, { DEMOGRAPHICS_ANCHOR } from './CategoryCard';
import ProgressRail from './ProgressRail';
import ReferralPills from './ReferralPills';
import { Tone, TONE_STYLES, WorkflowStatusBadge } from './StatusBadge';

export default function InboundReferralPage() {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId: string }>();
  const {
    workflow,
    isLoading,
    isError,
    writeValues,
    isWritingValues,
    completeAction,
    isCompletingAction,
    invokeAction,
    isInvokingAction,
  } = useWorkflow(workflowId);

  if (isLoading)
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  if (isError || !workflow)
    return (
      <Centered>
        <Body>We couldn’t load this referral.</Body>
      </Centered>
    );

  const patientName = workflow.patientName;
  const dob = simpleDate(workflow.valueFor('date_of_birth')?.value);
  const buildings = workflow.groups;
  const hasDemographics = ['first_name', 'last_name', 'date_of_birth'].every((n) => !!workflow.valueFor(n)?.value);

  return (
    <Page>
      <Page.Header>
        <HeaderInner>
          <TitleRow>
            <BackButton type='button' onClick={() => navigate(-1)} aria-label='Back'>
              <BackArrowIcon width={20} height={20} />
            </BackButton>
            <H2>{patientName}</H2>
            <WorkflowStatusBadge status={workflow.status} />
            {workflow.hasOutcome && <OutcomeChip data-testid='workflow-outcome'>{workflow.outcome}</OutcomeChip>}
          </TitleRow>
          {workflow.createdAt && <Subline>Received on {simpleDateAtTime(workflow.createdAt)}</Subline>}
          {(buildings.length > 0 || dob) && (
            <Chips>
              {buildings.map((group) => (
                <Chip key={group.id}>
                  {group.groupType?.displayName ? `${group.groupType.displayName}: ${group.name}` : group.name}
                </Chip>
              ))}
              {dob && <Chip>DOB: {dob}</Chip>}
            </Chips>
          )}
        </HeaderInner>
      </Page.Header>
      <Page.Content>
        <Layout>
          <RailColumn>
            <ProgressRail workflow={workflow} />
          </RailColumn>

          <MainColumn>
            <SectionCard id={DEMOGRAPHICS_ANCHOR} data-testid='demographics-card'>
              <CardHeader>
                <BodySmallBold>Patient details</BodySmallBold>
                {!workflow.isTerminal && (
                  <MiniPill $tone={hasDemographics ? 'success' : 'warning'}>
                    {hasDemographics ? 'Provided' : 'Action Needed'}
                  </MiniPill>
                )}
              </CardHeader>
              <CardBody>
                <ReferralPills
                  workflow={workflow}
                  onSave={writeValues}
                  isSaving={isWritingValues}
                  readOnly={workflow.isTerminal}
                />
              </CardBody>
            </SectionCard>

            {workflow.actionsByCategory().map(({ category, actions }) => (
              <CategoryCard
                key={category.key}
                category={category}
                actions={actions}
                workflow={workflow}
                onComplete={(actionId, outputs) => completeAction({ actionId, outputs })}
                isCompleting={isCompletingAction}
                onInvoke={(actionId, intent) => invokeAction({ actionId, invokeAction: intent })}
                isInvoking={isInvokingAction}
              />
            ))}
          </MainColumn>
        </Layout>
      </Page.Content>
    </Page>
  );
}

const Centered = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const HeaderInner = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${colors.black};
`;

const Subline = styled(BodySmall)`
  color: ${colors.black50};
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Chip = styled(BodySmall)`
  padding: 4px 12px;
  border-radius: 4px;
  background: ${colors.black05};
  color: ${colors.black75};
`;

const OutcomeChip = styled(BodySmallBold)`
  text-transform: capitalize;
  padding: 3px 12px;
  border-radius: 24px;
  background: ${colors.primaryBlue10};
  color: ${colors.primaryBlue};
`;

const Layout = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const RailColumn = styled.div`
  flex: 0 0 300px;
  max-width: 300px;

  @media (max-width: 900px) {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
  }
`;

const MainColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
`;

const SectionCard = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
`;

const CardBody = styled.div`
  padding: 0 16px 16px;
`;

const MiniPill = styled.span<{ $tone: Tone }>`
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  padding: 3px 12px;
  border-radius: 24px;
  background-color: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].color};
`;
